import { useDataExplorerAppearance } from './appearanceContext';
import * as Popover from '@radix-ui/react-popover';
import { Check, ChevronDown, Loader2, Search, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import type { DataExplorerRecordPickerProps, DataExplorerRecordPickerOption } from './types';
import { resolveDataExplorerAppearance } from './theme';
import { cn } from './utils';

const normalizeValues = (value: string | string[] | undefined, multiple: boolean): string[] => {
  const values = multiple ? (Array.isArray(value) ? value : value ? [value] : []) : typeof value === 'string' ? [value] : [];
  return Array.from(new Set(values.filter((item) => typeof item === 'string' && item.length > 0)));
};

const getOptionText = (option: DataExplorerRecordPickerOption) =>
  option.textValue ?? (typeof option.label === 'string' ? option.label : option.value);

export function DataExplorerRecordPicker({
  value,
  activeValue,
  options = [],
  createOption,
  multiple = false,
  placeholder = 'Select a record',
  triggerLabel,
  triggerIcon,
  searchPlaceholder = 'Search records',
  emptyState = 'No records found',
  loadingState,
  loadingMoreState,
  errorState,
  loading = false,
  loadingMore = false,
  disabled = false,
  open,
  defaultOpen = false,
  searchValue,
  defaultSearchValue = '',
  appearance,
  className,
  classNames,
  triggerProps,
  dataAttributes,
  onOpenChange,
  onListScroll,
  onSearchValueChange,
  onValueChange,
}: DataExplorerRecordPickerProps) {
  const theme = resolveDataExplorerAppearance(
    useDataExplorerAppearance(appearance),
  );
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const [uncontrolledSearch, setUncontrolledSearch] = useState(defaultSearchValue);
  const isControlledOpen = open !== undefined;
  const isOpen = isControlledOpen ? open : uncontrolledOpen;
  const isControlledSearch = searchValue !== undefined;
  const query = isControlledSearch ? searchValue : uncontrolledSearch;
  const selectedValues = useMemo(() => normalizeValues(value, multiple), [multiple, value]);
  const selectedSet = useMemo(() => new Set(selectedValues), [selectedValues]);
  const selectedOptions = selectedValues
    .map((selectedValue) => options.find((option) => option.value === selectedValue))
    .filter((option): option is DataExplorerRecordPickerOption => Boolean(option));
  const visibleOptions = createOption ? [createOption, ...options] : options;
  const triggerValue = selectedOptions.length
    ? multiple && selectedOptions.length > 1
      ? `${selectedOptions.slice(0, 2).map((option) => getOptionText(option)).join(', ')} +${selectedOptions.length - 2}`
      : getOptionText(selectedOptions[0])
    : placeholder;

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onOpenChange?.(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onOpenChange]);

  const setOpen = (nextOpen: boolean) => {
    if (disabled) return;
    if (!isControlledOpen) setUncontrolledOpen(nextOpen);
    onOpenChange?.(nextOpen);
  };

  const setQuery = (nextQuery: string) => {
    if (!isControlledSearch) setUncontrolledSearch(nextQuery);
    onSearchValueChange?.(nextQuery);
  };

  const handleOptionClick = (option: DataExplorerRecordPickerOption) => {
    if (disabled || option.disabled) return;
    if (multiple) {
      const nextValues = selectedSet.has(option.value)
        ? selectedValues.filter((selectedValue) => selectedValue !== option.value)
        : [...selectedValues, option.value];
      onValueChange?.(nextValues);
      return;
    }
    onValueChange?.(option.value);
    setOpen(false);
  };

  const removeValue = (selectedValue: string) => {
    if (disabled) return;
    onValueChange?.(selectedValues.filter((valueItem) => valueItem !== selectedValue));
  };

  return (
    <div {...dataAttributes?.root} className={cn('relative min-w-0 w-full', classNames?.root, className)}>
      <Popover.Root open={isOpen} onOpenChange={setOpen}>
        <Popover.Trigger asChild>
          <button
            {...triggerProps}
            {...dataAttributes?.trigger}
            type="button"
            disabled={disabled}
            aria-label={triggerLabel}
            aria-expanded={isOpen}
            className={cn(theme.slots.controlSelectTrigger, classNames?.trigger)}
          >
            {triggerIcon}
            <span {...dataAttributes?.triggerValue} className={cn(theme.slots.controlSelectValue, !selectedOptions.length && 'text-muted-foreground', classNames?.triggerValue)}>{triggerValue}</span>
            <ChevronDown className="h-4 w-4 shrink-0" aria-hidden="true" />
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            {...dataAttributes?.content}
            align="start"
            sideOffset={6}
            className={cn('z-50 w-[min(30rem,calc(100vw-2rem))] rounded-md border border-border bg-popover p-2 text-popover-foreground shadow-md', classNames?.content)}
            onOpenAutoFocus={(event) => event.preventDefault()}
          >
            <label className={cn('flex h-9 items-center gap-2 rounded-md border border-border bg-background px-2 text-sm', classNames?.search)}>
              <Search className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
              <input
                autoFocus
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={searchPlaceholder}
                aria-label={searchPlaceholder}
                className="min-w-0 flex-1 bg-transparent outline-none placeholder:text-muted-foreground"
              />
            </label>
            <div
              className={cn('mt-2 max-h-72 overflow-y-auto', classNames?.list)}
              {...dataAttributes?.list}
              role="listbox"
              aria-multiselectable={multiple || undefined}
              onScroll={onListScroll}
            >
              {loading ? (
                <div {...dataAttributes?.loading} className={cn('flex min-h-16 items-center justify-center gap-2 px-3 py-4 text-sm text-muted-foreground', classNames?.status)}>
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  {loadingState ?? 'Loading records...'}
                </div>
              ) : errorState ? (
                <div {...dataAttributes?.error} className={cn('px-3 py-4 text-sm text-destructive-text', classNames?.status)}>{errorState}</div>
              ) : visibleOptions.length === 0 ? (
                <div {...dataAttributes?.empty} className={cn('px-3 py-4 text-sm text-muted-foreground', classNames?.status)}>{emptyState}</div>
              ) : (
                <>
                  {visibleOptions.map((option) => {
                    const selected = selectedSet.has(option.value);
                    return (
                      <button
                        key={option.value}
                        {...dataAttributes?.option}
                        type="button"
                        role="option"
                        aria-selected={selected}
                        aria-current={activeValue !== undefined && option.value === activeValue ? 'true' : undefined}
                        disabled={option.disabled}
                        onClick={() => handleOptionClick(option)}
                        className={cn(
                          'flex w-full items-start gap-2 rounded-sm px-2.5 py-2 text-left text-sm transition-colors hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-50',
                          selected && 'bg-accent/60',
                          classNames?.option,
                        )}
                      >
                        <span className={cn('mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center', classNames?.optionIndicator)}>
                          {selected ? <Check className="h-4 w-4" aria-hidden="true" /> : null}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className={cn('block truncate', classNames?.optionLabel)}>{option.label}</span>
                          {option.description ? <span className={cn('mt-0.5 block truncate text-xs text-muted-foreground', classNames?.optionDescription)}>{option.description}</span> : null}
                        </span>
                      </button>
                    );
                  })}
                  {loadingMore ? (
                    <div
                      {...dataAttributes?.loadingMore}
                      className={cn(
                        'flex items-center justify-center gap-2 px-3 py-2 text-xs text-muted-foreground',
                        classNames?.status,
                      )}
                    >
                      <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                      {loadingMoreState ?? 'Loading more records...'}
                    </div>
                  ) : null}
                </>
              )}
            </div>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
      {multiple && selectedOptions.length > 0 ? (
        <div className={cn('mt-2 flex flex-wrap gap-1.5', classNames?.selectedList)}>
          {selectedOptions.map((option) => (
            <span key={option.value} className={cn('inline-flex max-w-full items-center gap-1 rounded-full border border-border bg-muted px-2 py-1 text-xs', classNames?.selectedItem)}>
              <span className={cn('min-w-0 truncate', classNames?.selectedItemLabel)}>{option.label}</span>
              <button
                type="button"
                aria-label={`Remove ${getOptionText(option)}`}
                onClick={() => removeValue(option.value)}
                className={cn('inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:bg-accent hover:text-foreground', classNames?.selectedItemRemove)}
              >
                <X className="h-3 w-3" aria-hidden="true" />
              </button>
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}
